import { ReplayJournal } from '@/components/admin/reglages/replay-journal';
import { ReplaySettingsPanel } from '@/components/admin/reglages/replay-settings-panel';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { saveFailureSentence } from '@/lib/replay-settings';
import { saveReplaySettings } from '@/lib/replay-settings-api';
import {
  REPLAY_SETTINGS_CHANGES_KEY,
  REPLAY_SETTINGS_KEY,
  useReplaySettings,
  useReplaySettingsChanges,
} from '@/lib/use-replay-settings';
import type { ReplaySettingsInput } from '@matter_extractor/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * Réglages > Rejeu (docs/specs/rejeu.md §10.3, issue #193): the EIGHTH tab of
 * Réglages, after Méthode (#196, which came in right after Noyaux) - the three
 * texts of the instruction Claude follows when it plays the person of a replay,
 * and the journal of their saves.
 *
 * ## The tab exists only when the replay is open (§4)
 *
 * `GET /api/replay-settings` answers 404 on a server where `REPLAY_ENABLED` is
 * not `true`, and the shell draws the tab from the same read. A direct visit to
 * this address on such a server therefore lands on the state « introuvable » the
 * administration gives every address that names nothing - not on an empty
 * editor whose save could only fail.
 *
 * ## Not versioned, but journalled
 *
 * Unlike the texts of the noyaux there is no history to walk back here: the
 * save overwrites, and the journal is what keeps the move. Each replay freezes
 * its own copy of the instruction when it starts, so retouching these texts
 * touches no replay already begun - the page says so, and so does the box that
 * announces the save.
 *
 * ## One key per save, and the panel remounts on it
 *
 * The régime of `reglages.regles.tsx`: the panel seeds its draft from its props
 * and never by an effect, so a save that LANDED remounts it on what the base
 * now holds (the draft has just become the text in force). A save that failed
 * does not: the three paragraphs the operator typed are the only place they
 * exist, and the failure is said under the box with the button still live.
 */

export const Route = createFileRoute('/admin/reglages/rejeu')({
  component: ReglagesRejeuPage,
});

/**
 * EXPORTED for `tests/reglages-rejeu.test.tsx`, and for nothing else:
 * `vitest.config.ts` leaves `src/routes/**` out of the coverage REPORT, not out
 * of the suite, and what this component decides - the closed state, the
 * invalidations after a save, the remount - cannot be seen from the panel.
 */
export function ReglagesRejeuPage() {
  const { email } = useAdminSession();
  const queryClient = useQueryClient();
  const [generation, setGeneration] = useState(0);

  const settings = useReplaySettings();
  // The journal is read once the replay is known to be open: on a closed
  // server it would only collect a second 404.
  const changes = useReplaySettingsChanges(settings.data != null);

  const save = useMutation({
    mutationFn: (input: ReplaySettingsInput) => saveReplaySettings(input),
    onSuccess: async (saved) => {
      // The answer IS the new state of the base: written to the cache first so
      // the remounted panel is seeded on it, then both reads are made fresh -
      // the journal gained a line and `replayCount` may have moved.
      queryClient.setQueryData([REPLAY_SETTINGS_KEY, email], saved);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [REPLAY_SETTINGS_KEY] }),
        queryClient.invalidateQueries({ queryKey: [REPLAY_SETTINGS_CHANGES_KEY] }),
      ]);
      setGeneration((count) => count + 1);
    },
  });

  // A 401 is the door reopening, not an error of this screen (T6).
  const rejected = useRejectOn401(settings.error);

  if (settings.isPending) {
    return (
      <div className="flex justify-center py-16">
        <PmsSpinner label="Chargement de la consigne du rejeu" />
      </div>
    );
  }

  // The replay is closed on this server: the tab does not exist, and neither
  // does this address. Same sentence and same way out as the editors.
  if (settings.data === null) {
    return (
      <p data-testid="rejeu-introuvable" className="text-gray-700">
        Cette page n’existe pas : le rejeu n’est pas ouvert sur ce serveur.{' '}
        <Link to="/admin/reglages/modeles" className="text-bleu-nuit underline underline-offset-2">
          Revenir aux réglages
        </Link>
        .
      </p>
    );
  }

  if (settings.data === undefined) {
    return rejected ? null : (
      <p role="alert" className="text-rouge-deep text-sm">
        La consigne du rejeu n’a pas pu être lue. Rechargez l’écran.
      </p>
    );
  }

  return (
    <section data-testid="reglages-rejeu" className="flex flex-col gap-5 pb-16">
      <header className="flex flex-col gap-1.5">
        <h2 className="font-semibold text-[22px] text-gray-900 leading-[30px]">Rejeu</h2>
        <p className="max-w-[96ch] text-gray-600 text-sm leading-[21px]">
          La consigne que suit Claude quand il joue la personne d’un rejeu, par l’assistant MCP de
          ce serveur. Elle est servie dans cet ordre : les règles communes, le paragraphe du mode
          choisi au démarrage, puis la source.{' '}
          <strong className="font-semibold text-gray-700">Chaque rejeu en garde une copie</strong> :
          retoucher ces textes ne change aucun rejeu commencé. Pas de versions ici, mais un journal
          de chaque enregistrement.
        </p>
      </header>

      <ReplaySettingsPanel
        // The key is the generation of the LAST SAVE THAT LANDED, never the
        // data itself: a refetch under a typing hand must not remount.
        key={generation}
        settings={settings.data}
        saving={save.isPending}
        failure={save.isError ? saveFailureSentence(save.error) : null}
        onSave={(input) => save.mutate(input)}
        onRevert={() => save.reset()}
      />

      {changes.data === undefined ? (
        <JournalPending failed={changes.isError} />
      ) : (
        <ReplayJournal changes={changes.data.changes} />
      )}
    </section>
  );
}

/** The journal is still being read, or could not be: the texts above are not held back for it. */
function JournalPending({ failed }: { readonly failed: boolean }) {
  return failed ? (
    <p role="alert" data-testid="rejeu-journal-echec" className="text-rouge-deep text-sm">
      Le journal n’a pas pu être lu. Rechargez l’écran.
    </p>
  ) : (
    <div className="flex justify-center py-6">
      <PmsSpinner label="Chargement du journal" />
    </div>
  );
}
